import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Gift, Coins, Flag, TextT, Ticket, Diamond, Heart } from "@phosphor-icons/react";

const PRIZE_ICON = {
  credits:              { Icon: Coins,   tint: "text-blue-600 dark:text-blue-400" },
  banner_impressions:   { Icon: Flag,    tint: "text-fuchsia-600 dark:text-fuchsia-400" },
  text_ad_impressions:  { Icon: TextT,   tint: "text-cyan-600 dark:text-cyan-400" },
  surf_drawn_tickets:   { Icon: Ticket,  tint: "text-amber-600 dark:text-amber-400" },
  diamond_emeralds:     { Icon: Diamond, tint: "text-emerald-600 dark:text-emerald-400" },
  heart_chrysolite:     { Icon: Heart,   tint: "text-rose-600 dark:text-rose-400" },
};

const PAGE_SIZE = 20;

export default function Statistics() {
  const [stats, setStats] = useState(null);
  const [txs, setTxs] = useState([]);
  // Unified bonus history — spans every currency (credits, banners, text ads,
  // tickets, emeralds, chrysolite) via /collections/mine which joins
  // prize_claims + currency_adjustments server-side.
  const [prizes, setPrizes] = useState([]);
  const [prizesTotal, setPrizesTotal] = useState(0);
  const [prizesOffset, setPrizesOffset] = useState(0);
  const [totals, setTotals] = useState({});
  const [loadingPrizes, setLoadingPrizes] = useState(false);

  useEffect(() => {
    api.get("/credits/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/credits/transactions").then((r) => setTxs(r.data)).catch(() => {});
    loadPrizes(0);
  }, []);

  const loadPrizes = async (offset) => {
    setLoadingPrizes(true);
    try {
      const { data } = await api.get(`/collections/mine?limit=${PAGE_SIZE}&offset=${offset}`);
      setPrizes(data.history?.items || []);
      setPrizesTotal(data.history?.total || 0);
      setPrizesOffset(offset);
      setTotals(data.totals || {});
    } catch { /* ignore */ }
    finally { setLoadingPrizes(false); }
  };

  const grouped = {};
  txs.forEach((t) => { grouped[t.type] = (grouped[t.type] || 0) + t.amount; });

  const prizePages = Math.max(1, Math.ceil(prizesTotal / PAGE_SIZE));
  const prizePage = Math.floor(prizesOffset / PAGE_SIZE) + 1;

  return (
    <DashboardLayout>
      <div className="mb-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Analytics</div>
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Statistics</h1>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-5">
        {[
          { label: "Credits", value: stats?.credits?.toFixed(1) },
          { label: "XP", value: stats?.xp },
          { label: "Total surfs", value: stats?.total_surfs },
          { label: "Today's surfs", value: stats?.daily_surfs },
        ].map((c) => (
          <div key={c.label} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
            <div className="text-3xl font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{c.value ?? "—"}</div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Collection totals — always visible so members know what they've built up. */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { key: "surf_drawn_tickets", label: "Tickets", tint: "from-amber-500 to-orange-600", Icon: Ticket },
          { key: "diamond_emeralds",   label: "Diamonds", tint: "from-emerald-500 to-green-700", Icon: Diamond },
          { key: "heart_chrysolite",   label: "Chrysolite", tint: "from-rose-500 to-pink-700", Icon: Heart },
        ].map((c) => (
          <div key={c.key} data-testid={`collection-${c.key}`} className={`rounded-2xl bg-gradient-to-br ${c.tint} text-white p-4 shadow-sm`}>
            <div className="flex items-center justify-between">
              <c.Icon size={22} weight="fill" className="opacity-90" />
              <div className="text-xs font-black uppercase tracking-widest opacity-80">{c.label}</div>
            </div>
            <div className="mt-2 text-3xl font-black" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{Number(totals?.[c.key] || 0).toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Bonus history — every Prize Page reward + manual admin grant, unified
          across every currency the site supports (Credits, Banner Impressions,
          Text Ad Impressions, Surf Drawn Tickets, Diamond Emeralds, Heart
          Shaped Chrysolite). */}
      <div className="rounded-2xl border border-yellow-200 dark:border-yellow-500/30 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-500/5 dark:to-orange-500/5 mb-5" data-testid="bonus-history-card">
        <div className="p-5 flex items-center justify-between border-b border-yellow-200/70 dark:border-yellow-500/20">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 text-white flex items-center justify-center shadow-sm">
              <Gift size={20} weight="fill" />
            </div>
            <div>
              <div className="font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Bonus History</div>
              <div className="text-xs text-slate-500">Every prize you've claimed or been granted, across every currency</div>
            </div>
          </div>
          <div className="text-xs font-black uppercase tracking-widest text-slate-500">{prizesTotal} entr{prizesTotal !== 1 ? "ies" : "y"}</div>
        </div>
        <div className="divide-y divide-yellow-200/50 dark:divide-yellow-500/10">
          {loadingPrizes && <div className="p-5 text-sm text-slate-500 text-center">Loading…</div>}
          {!loadingPrizes && prizes.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              <Gift size={26} weight="duotone" className="mx-auto text-yellow-500 mb-2" />
              No prizes yet. Keep surfing to hit your first milestone!
            </div>
          )}
          {prizes.map((p, idx) => {
            const meta = PRIZE_ICON[p.type] || { Icon: Gift, tint: "text-slate-500" };
            const Icon = meta.Icon;
            const positive = Number(p.amount) >= 0;
            return (
              <div key={`${p.created_at}-${idx}`} data-testid="bonus-history-row" className="p-4 flex items-center gap-3">
                <Icon size={22} weight="fill" className={meta.tint} />
                <div className="flex-1 min-w-0">
                  <div className={`font-bold truncate ${positive ? "text-slate-900 dark:text-white" : "text-red-600 dark:text-red-400"}`}>
                    {positive ? "+" : ""}{Number(p.amount).toLocaleString()} {p.type_label}
                  </div>
                  <div className="text-xs text-slate-500 capitalize">
                    {new Date(p.created_at).toLocaleString()} · {p.source_label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {prizesTotal > PAGE_SIZE && (
          <div className="p-3 flex items-center justify-between border-t border-yellow-200/70 dark:border-yellow-500/20">
            <button
              data-testid="bonus-history-prev"
              disabled={prizesOffset === 0 || loadingPrizes}
              onClick={() => loadPrizes(Math.max(0, prizesOffset - PAGE_SIZE))}
              className="text-xs font-bold rounded-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 disabled:opacity-50"
            >
              Prev
            </button>
            <div className="text-xs text-slate-500">Page {prizePage} of {prizePages}</div>
            <button
              data-testid="bonus-history-next"
              disabled={prizesOffset + PAGE_SIZE >= prizesTotal || loadingPrizes}
              onClick={() => loadPrizes(prizesOffset + PAGE_SIZE)}
              className="text-xs font-bold rounded-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <h2 className="font-black text-slate-900 dark:text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>By category</h2>
          <div className="space-y-2">
            {Object.entries(grouped).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="capitalize text-slate-700 dark:text-slate-200">{k.replace(/_/g, " ")}</span>
                <span className={`font-black ${v > 0 ? "text-green-600" : "text-red-600"}`}>{v > 0 ? "+" : ""}{v.toFixed(1)}</span>
              </div>
            ))}
            {Object.keys(grouped).length === 0 && <div className="text-sm text-slate-500">No transactions yet.</div>}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div className="p-5 border-b border-slate-200 dark:border-slate-800 font-black" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Recent transactions</div>
          <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-96 overflow-y-auto">
            {txs.slice(0, 30).map((t) => (
              <div key={t.id} className="p-3 flex justify-between text-sm">
                <div>
                  <div className="font-semibold text-slate-900 dark:text-white capitalize">{t.type.replace(/_/g, " ")}</div>
                  <div className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</div>
                </div>
                <div className={`font-black ${t.amount > 0 ? "text-green-600" : "text-red-600"}`}>{t.amount > 0 ? "+" : ""}{t.amount.toFixed(2)}</div>
              </div>
            ))}
            {txs.length === 0 && <div className="p-5 text-sm text-slate-500">No activity yet.</div>}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
